import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, Plus, Trash2, Calendar, Percent, X, Ticket, Copy, Pencil, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../config';
import { exportCSV, exportPDF } from '../../utils/exportUtils';

const TEMPLATE_LABELS = {
  kickstarter: 'Kickstarter',
  black_friday: 'Black Friday',
  cyber_monday: 'Cyber Monday',
  summer_sales: "Soldes d'Été",
  winter_sales: "Soldes d'Hiver",
  christmas: 'Noël',
  halloween: 'Halloween',
  custom: 'Personnalisée',
};

const PACKAGE_LABELS = {
  early_bird: 'Early Bird',
  kickstarter_standard: 'Standard',
  kickstarter_standard_mobile: 'Standard + Mobile',
  kickstarter_founder: 'Founder',
  kickstarter_founder_mobile: 'Founder + Mobile',
};

export function AdminCampaigns({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/campaigns`, { headers }),
        axios.get(`${API}/admin/promo-codes`, { headers }),
      ]);
      setCampaigns(cRes.data.campaigns);
      setPromoCodes(pRes.data.promo_codes);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteCampaign = async (id) => {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${id}`, { headers });
      toast.success('Campagne supprimée');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const deletePromo = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { headers });
      toast.success('Code supprimé');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const togglePromoStatus = async (promo) => {
    const newStatus = promo.status === 'active' ? 'disabled' : 'active';
    try {
      await axios.put(`${API}/admin/promo-codes/${promo.id}`, { status: newStatus }, { headers });
      toast.success(`Code ${newStatus === 'active' ? 'activé' : 'désactivé'}`);
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'draft' : 'active';
    try {
      await axios.put(`${API}/admin/campaigns/${campaign.id}`, { status: newStatus }, { headers });
      toast.success(`Campagne ${newStatus === 'active' ? 'activée' : 'désactivée'}`);
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div data-testid="admin-campaigns-section">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setTab('campaigns')} className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${tab === 'campaigns' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-campaigns">
          Campagnes
        </button>
        <button onClick={() => setTab('promos')} className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${tab === 'promos' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-promos">
          Codes Promo
        </button>
      </div>

      {loading ? <p className="text-muted-foreground text-sm">Chargement...</p> : tab === 'campaigns' ? (
        <div className="space-y-3">
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => { const d = campaigns.map(c => ({
              Nom: c.name, Statut: c.status,
              Réduction: `${c.discount_value}${c.discount_type === 'percentage' ? '%' : ' EUR'}`,
              Début: c.start_date?.slice(0, 10), Fin: c.end_date?.slice(0, 10),
            })); exportCSV(d, 'campagnes'); }} className="border-border text-muted-foreground h-8" data-testid="export-campaigns-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => { const d = campaigns.map(c => ({
              Nom: c.name, Statut: c.status,
              Réduction: `${c.discount_value}${c.discount_type === 'percentage' ? '%' : ' EUR'}`,
              Début: c.start_date?.slice(0, 10), Fin: c.end_date?.slice(0, 10),
            })); exportPDF(d, 'campagnes', 'Campagnes Marketing'); }} className="border-border text-muted-foreground h-8" data-testid="export-campaigns-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
            <Button size="sm" onClick={() => setShowCreateCampaign(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-campaign-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle campagne
            </Button>
          </div>
          {campaigns.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">Aucune campagne. Créez-en une !</p>
          ) : campaigns.map(c => (
            <div key={c.id} className="p-4 rounded-xl bg-secondary/50 border border-border/50" data-testid={`campaign-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#3B82F6' }} />
                  <span className="text-foreground font-medium text-sm">{c.name}</span>
                  <span className="text-xs text-muted-foreground">({TEMPLATE_LABELS[c.template] || c.template})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    c.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' :
                    c.status === 'scheduled' ? 'bg-blue-500/10 text-blue-400' :
                    c.status === 'expired' ? 'bg-red-500/10 text-red-400' :
                    'bg-secondary text-muted-foreground'
                  }`}>{c.status}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-muted-foreground hover:text-foreground text-xs" onClick={() => toggleCampaignStatus(c)}>
                    {c.status === 'active' ? 'Pause' : 'Activer'}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-blue-400 hover:text-blue-300" onClick={() => setEditingCampaign(c)} data-testid={`edit-campaign-${c.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1 text-red-400 hover:text-red-300" onClick={() => deleteCampaign(c.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-3">
                <span className="flex items-center gap-1"><Percent className="w-3 h-3" />{c.discount_value}{c.discount_type === 'percentage' ? '%' : ' EUR'}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(c.start_date).toLocaleDateString('fr-FR')} - {new Date(c.end_date).toLocaleDateString('fr-FR')}</span>
                <span>{c.promo_codes_count || 0} code(s)</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => { const d = promoCodes.map(p => ({
              Code: p.code, Statut: p.status, Réduction: `${p.discount_value}${p.discount_type === 'percentage' ? '%' : ' EUR'}`,
              Début: p.valid_from?.slice(0, 10), Fin: p.valid_until?.slice(0, 10),
              Utilisations: `${p.uses_count}/${p.max_uses || 'illimité'}`,
            })); exportCSV(d, 'codes_promo'); }} className="border-border text-muted-foreground h-8" data-testid="export-promos-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => { const d = promoCodes.map(p => ({
              Code: p.code, Statut: p.status, Réduction: `${p.discount_value}${p.discount_type === 'percentage' ? '%' : ' EUR'}`,
              Début: p.valid_from?.slice(0, 10), Fin: p.valid_until?.slice(0, 10),
              Utilisations: `${p.uses_count}/${p.max_uses || 'illimité'}`,
            })); exportPDF(d, 'codes_promo', 'Codes Promo'); }} className="border-border text-muted-foreground h-8" data-testid="export-promos-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
            <Button size="sm" onClick={() => setShowCreatePromo(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-promo-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouveau code
            </Button>
          </div>
          {promoCodes.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">Aucun code promo.</p>
          ) : promoCodes.map(p => (
            <div key={p.id} className="p-3 rounded-xl bg-secondary/50 border border-border/50 flex items-center justify-between" data-testid={`promo-${p.id}`}>
              <div className="flex items-center gap-3">
                <Ticket className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-mono font-medium text-sm">{p.code}</span>
                    <button onClick={() => { navigator.clipboard.writeText(p.code); toast.success('Copie !'); }} className="text-muted-foreground hover:text-foreground">
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {p.discount_value}{p.discount_type === 'percentage' ? '%' : ' EUR'} |{' '}
                    {new Date(p.valid_from).toLocaleDateString('fr-FR')} - {new Date(p.valid_until).toLocaleDateString('fr-FR')} |{' '}
                    {p.uses_count}/{p.max_uses || 'illimité'} utilisations
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : p.status === 'expired' ? 'bg-red-500/10 text-red-400' : 'bg-secondary text-muted-foreground'}`}>{p.status}</span>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={() => togglePromoStatus(p)}>
                  {p.status === 'active' ? 'Désactiver' : 'Activer'}
                </Button>
                <Button variant="ghost" size="sm" className="h-6 px-1 text-red-400" onClick={() => deletePromo(p.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateCampaign && <CreateCampaignModal token={token} onClose={() => setShowCreateCampaign(false)} onCreated={() => { setShowCreateCampaign(false); load(); }} />}
      {showCreatePromo && <CreatePromoModal token={token} campaigns={campaigns} onClose={() => setShowCreatePromo(false)} onCreated={() => { setShowCreatePromo(false); load(); }} />}
      {editingCampaign && <EditCampaignModal token={token} campaign={editingCampaign} onClose={() => setEditingCampaign(null)} onUpdated={() => { setEditingCampaign(null); load(); }} />}
    </div>
  );
}

function CreateCampaignModal({ token, onClose, onCreated }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [tpl, setTpl] = useState('custom');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [campaignDescriptions, setCampaignDescriptions] = useState({});
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [sending, setSending] = useState(false);

  const TEMPLATES_DATA = {
    black_friday: { name: 'Black Friday', desc: 'Offre exceptionnelle Black Friday - réductions massives', discount: 40, color: '#000000' },
    cyber_monday: { name: 'Cyber Monday', desc: 'Offre Cyber Monday - exclusivité en ligne', discount: 35, color: '#00D4FF' },
    summer_sales: { name: "Soldes d'Été", desc: "Profitez des soldes d'été sur HexoPDF", discount: 25, color: '#FF6B35' },
    winter_sales: { name: "Soldes d'Hiver", desc: "Offres spéciales soldes d'hiver", discount: 30, color: '#4A90D9' },
    christmas: { name: 'Noël', desc: 'Offre spéciale Noël - le cadeau parfait', discount: 30, color: '#C41E3A' },
    halloween: { name: 'Halloween', desc: 'Promo Halloween - offre effrayante', discount: 20, color: '#FF6600' },
    custom: { name: '', desc: '', discount: 10, color: '#3B82F6' },
  };

  const applyTemplate = (key) => {
    setTpl(key);
    const t = TEMPLATES_DATA[key];
    if (key !== 'custom') {
      setName(t.name);
      setDesc(t.desc);
      setDiscountValue(t.discount);
      setColor(t.color);
    }
  };

  const handleCreate = async () => {
    if (!name || !startDate || !endDate) return toast.error('Nom et dates requis');
    setSending(true);
    try {
      await axios.post(`${API}/admin/campaigns`, {
        template: tpl, name, description: desc,
        descriptions: Object.keys(campaignDescriptions).length > 0 ? campaignDescriptions : undefined,
        discount_type: discountType, discount_value: discountValue,
        start_date: new Date(startDate).toISOString(), end_date: new Date(endDate).toISOString(),
        color, status: 'draft',
      }, { headers });
      toast.success('Campagne créée');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="create-campaign-modal">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground font-medium">Nouvelle campagne</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Modèle</label>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.entries(TEMPLATE_LABELS).map(([k, v]) => (
                <button key={k} onClick={() => applyTemplate(k)}
                  className={`px-2 py-1.5 rounded-lg text-xs text-center transition-colors ${tpl === k ? 'bg-blue-600 text-white' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Nom</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="campaign-name" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Description</label>
            <input value={desc} onChange={e => setDesc(e.target.value)} className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" />
          </div>
          <details className="group">
            <summary className="text-xs text-blue-400 cursor-pointer hover:text-blue-300 mb-2">Traductions de la description (optionnel)</summary>
            <div className="space-y-1.5 pl-2 border-l-2 border-blue-500/20">
              {desc && (
                <button type="button" onClick={() => {
                  const filled = {};
                  ['fr','en','es','de','it','pt','nl','pl','tr','ru','ja','zh','ar','hi'].forEach(code => {
                    filled[code] = campaignDescriptions[code] || desc;
                  });
                  setCampaignDescriptions(filled);
                }} className="text-[10px] text-blue-400 hover:text-blue-300 mb-1 underline" data-testid="prefill-descriptions-btn">
                  Pré-remplir les champs vides avec la description
                </button>
              )}
              {['fr','en','es','de','it','pt','nl','pl','tr','ru','ja','zh','ar','hi'].map(code => (
                <div key={code} className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground w-5 uppercase">{code}</span>
                  <input value={campaignDescriptions[code] || ''} onChange={e => setCampaignDescriptions(prev => ({...prev, [code]: e.target.value}))}
                    placeholder={`Description en ${code.toUpperCase()}`}
                    className="flex-1 px-2 py-1 bg-secondary/50 border border-border rounded text-xs text-foreground" />
                </div>
              ))}
            </div>
          </details>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <select value={discountType} onChange={e => setDiscountType(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="campaign-discount-type">
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (EUR)</option>
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs text-muted-foreground mb-1 block">Valeur</label>
              <input type="number" min={1} value={discountValue} onChange={e => setDiscountValue(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="campaign-discount-value" />
            </div>
            <div className="w-16">
              <label className="text-xs text-muted-foreground mb-1 block">Couleur</label>
              <input type="color" value={color} onChange={e => setColor(e.target.value)}
                className="w-full h-[38px] bg-secondary border border-border rounded-lg cursor-pointer" />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Date début</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="campaign-start-date" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Date fin</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="campaign-end-date" />
            </div>
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreate} disabled={sending} data-testid="campaign-create-submit">
            {sending ? 'Création...' : 'Créer la campagne'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreatePromoModal({ token, campaigns, onClose, onCreated }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [code, setCode] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(10);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [packages, setPackages] = useState([]);
  const [sending, setSending] = useState(false);

  const allPackages = Object.keys(PACKAGE_LABELS);

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let c = 'HEX-';
    for (let i = 0; i < 6; i++) c += chars[Math.floor(Math.random() * chars.length)];
    setCode(c);
  };

  const handleCreate = async () => {
    if (!code || !validFrom || !validUntil) return toast.error('Code et dates requis');
    setSending(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code, campaign_id: campaignId || null,
        discount_type: discountType, discount_value: discountValue,
        valid_from: new Date(validFrom).toISOString(), valid_until: new Date(validUntil).toISOString(),
        max_uses: maxUses ? parseInt(maxUses) : null,
        applicable_packages: packages.length > 0 ? packages : [],
      }, { headers });
      toast.success('Code créé');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="create-promo-modal">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground font-medium">Nouveau code promo</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Code</label>
            <div className="flex gap-2">
              <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="HEX-XXXXXX"
                className="flex-1 px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground font-mono" data-testid="promo-code-input" />
              <Button variant="outline" size="sm" onClick={generateCode} className="border-border text-muted-foreground">Générer</Button>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Campagne associée (optionnel)</label>
            <select value={campaignId} onChange={e => setCampaignId(e.target.value)}
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground">
              <option value="">Aucune</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <select value={discountType} onChange={e => setDiscountType(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="promo-discount-type">
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (EUR)</option>
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs text-muted-foreground mb-1 block">Valeur</label>
              <input type="number" min={1} value={discountValue} onChange={e => setDiscountValue(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="promo-discount-value" />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Valide du</label>
              <input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="promo-valid-from" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Valide jusqu'au</label>
              <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="promo-valid-until" />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Utilisations max (vide = illimité)</label>
            <input type="number" min={1} value={maxUses} onChange={e => setMaxUses(e.target.value)} placeholder="Illimite"
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Applicable aux packages (vide = tous)</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {allPackages.map(pkg => (
                <button key={pkg} onClick={() => setPackages(prev => prev.includes(pkg) ? prev.filter(p => p !== pkg) : [...prev, pkg])}
                  className={`px-2 py-1 rounded text-xs transition-colors ${packages.includes(pkg) ? 'bg-blue-600 text-white' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                  {PACKAGE_LABELS[pkg]}
                </button>
              ))}
            </div>
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreate} disabled={sending} data-testid="promo-create-submit">
            {sending ? 'Création...' : 'Créer le code promo'}
          </Button>
        </div>
      </div>
    </div>
  );
}


function EditCampaignModal({ token, campaign, onClose, onUpdated }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [name, setName] = useState(campaign.name || '');
  const [desc, setDesc] = useState(campaign.description || '');
  const [editDescriptions, setEditDescriptions] = useState(campaign.descriptions || {});
  const [discountType, setDiscountType] = useState(campaign.discount_type || 'percentage');
  const [discountValue, setDiscountValue] = useState(campaign.discount_value || 10);
  const [startDate, setStartDate] = useState(campaign.start_date?.slice(0, 10) || '');
  const [endDate, setEndDate] = useState(campaign.end_date?.slice(0, 10) || '');
  const [color, setColor] = useState(campaign.color || '#3B82F6');
  const [status, setStatus] = useState(campaign.status || 'draft');
  const [sending, setSending] = useState(false);

  const handleUpdate = async () => {
    if (!name || !startDate || !endDate) return toast.error('Nom et dates requis');
    setSending(true);
    try {
      await axios.put(`${API}/admin/campaigns/${campaign.id}`, {
        name, description: desc,
        descriptions: Object.keys(editDescriptions).length > 0 ? editDescriptions : undefined,
        discount_type: discountType, discount_value: discountValue,
        start_date: new Date(startDate).toISOString(), end_date: new Date(endDate).toISOString(),
        color, status,
      }, { headers });
      toast.success('Campagne mise à jour');
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="edit-campaign-modal">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground font-medium">Modifier la campagne</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Nom</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="edit-campaign-name" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Description</label>
            <input value={desc} onChange={e => setDesc(e.target.value)} className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="edit-campaign-desc" />
          </div>
          <details className="group">
            <summary className="text-xs text-blue-400 cursor-pointer hover:text-blue-300 mb-2">Traductions de la description (optionnel)</summary>
            <div className="space-y-1.5 pl-2 border-l-2 border-blue-500/20">
              {desc && (
                <button type="button" onClick={() => {
                  const filled = {};
                  ['fr','en','es','de','it','pt','nl','pl','tr','ru','ja','zh','ar','hi'].forEach(code => {
                    filled[code] = editDescriptions[code] || desc;
                  });
                  setEditDescriptions(filled);
                }} className="text-[10px] text-blue-400 hover:text-blue-300 mb-1 underline" data-testid="prefill-edit-descriptions-btn">
                  Pré-remplir les champs vides avec la description
                </button>
              )}
              {['fr','en','es','de','it','pt','nl','pl','tr','ru','ja','zh','ar','hi'].map(code => (
                <div key={code} className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground w-5 uppercase">{code}</span>
                  <input value={editDescriptions[code] || ''} onChange={e => setEditDescriptions(prev => ({...prev, [code]: e.target.value}))}
                    placeholder={`Description en ${code.toUpperCase()}`}
                    className="flex-1 px-2 py-1 bg-secondary/50 border border-border rounded text-xs text-foreground" />
                </div>
              ))}
            </div>
          </details>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <select value={discountType} onChange={e => setDiscountType(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="edit-campaign-discount-type">
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (EUR)</option>
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs text-muted-foreground mb-1 block">Valeur</label>
              <input type="number" min={1} value={discountValue} onChange={e => setDiscountValue(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="edit-campaign-discount-value" />
            </div>
            <div className="w-16">
              <label className="text-xs text-muted-foreground mb-1 block">Couleur</label>
              <input type="color" value={color} onChange={e => setColor(e.target.value)}
                className="w-full h-[38px] bg-secondary border border-border rounded-lg cursor-pointer" />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Date début</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="edit-campaign-start-date" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Date fin</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="edit-campaign-end-date" />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Statut</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-foreground" data-testid="edit-campaign-status">
              <option value="draft">Brouillon</option>
              <option value="scheduled">Planifiée</option>
              <option value="active">Active</option>
            </select>
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={handleUpdate} disabled={sending} data-testid="edit-campaign-submit">
            {sending ? 'Mise à jour...' : 'Enregistrer les modifications'}
          </Button>
        </div>
      </div>
    </div>
  );
}
